<template>
  <div class="ms-planner-form">
    <m-content
      v-if="!getPlanDetailsLoading && hasError"
      :padding-top="0"
      :padding-x="11"
      :padding-bottom="2"
    >
      <data-source-query-error
        :data-source="dataSource"
        :error="error"
      />
    </m-content>
    <template v-if="!hasConnectionError">
      <m-content
        :padding-top="0"
        :padding-x="11"
        :padding-bottom="2"
      >
        <div class="_item">
          <div class="_label">
            {{ $t('msPlannerForm.aggregationLabel') }}
          </div>
          <m-select
            v-model:value="localAggregation"
            :items="aggregationModes"
            :placeholder="$t('general.all')"
            return-object
            full-width
            match-trigger-width
            has-tooltip
            tooltip-placement="right"
            @change="changeAggregation"
          >
            <template #item-tooltip="{ item }">
              {{ $t(item.item.tooltip) }}
            </template>
          </m-select>
        </div>
        <div class="_item">
          <div class="_label">
            {{ $t('msPlannerForm.selectPlanLabel') }}
          </div>
          <m-select
            v-model:value="localQuery.planId"
            :items="dataSource[customDatasourceProperty.msPlannerPlans]"
            value-key="id"
            item-text="name"
            full-width
            match-trigger-width
            show-search
            @change="changePlan"
          />
        </div>
        <template
          v-if="selectedPlan !== null && dataSource[customDatasourceProperty.msPlannerPlans].length > 0"
        >
          <div class="_item">
            <m-radio-group
              :value="localQuery.filterMode"
              :options="filterModes"
              inline
              @change="changeFilterMode"
            />
          </div>
          <template v-if="localQuery.filterMode === msPlannerFilterMode.filter">
            <div class="_item">
              <div class="_label">
                {{ $t('msPlannerForm.selectBucketsLabel') }}
              </div>
              <m-select
                v-model:value="localQuery.bucketIDs"
                :items="buckets"
                :loading="getPlanDetailsLoading"
                :placeholder="$t('general.all')"
                value-key="id"
                item-text="name"
                full-width
                match-trigger-width
                multiple
                @change="changeBuckets"
              />
            </div>
            <div class="_item">
              <div class="_label">
                {{ $t('msPlannerForm.selectLabelsLabel') }}
              </div>
              <m-select
                v-model:value="localQuery.labelIDs"
                :items="labels"
                :loading="getPlanDetailsLoading"
                :placeholder="$t('general.all')"
                value-key="id"
                item-text="name"
                full-width
                match-trigger-width
                multiple
                @change="changeLabels"
              />
            </div>
          </template>
          <template v-else>
            <div class="_item">
              <div class="_label">
                {{ $t('msPlannerForm.selectTasksLabel') }}
              </div>
              <m-select
                v-model:value="localQuery.taskIDs"
                :items="tasks"
                :loading="getPlanDetailsLoading"
                :placeholder="$t('general.all')"
                value-key="id"
                item-text="name"
                full-width
                match-trigger-width
                show-search
                multiple
                @change="changeTasks"
              />
            </div>
          </template>
        </template>
        <div
          v-if="localQuery.planId !== null"
          class="_item"
          :style="{ marginTop: '2rem' }"
        >
          <always-sync-checkbox v-model:value="localQuery.alwaysSync" />
        </div>
      </m-content>
      <m-divider xs />
      <template v-if="localQuery.uid === undefined">
        <m-content padding-small>
          <div class="_actions">
            <div class="_btns">
              <m-btn
                class="_btn"
                @click="$emit('close')"
              >
                {{ $t('general.cancel') }}
              </m-btn>
              <m-btn
                color="primary"
                class="_btn"
                :loading="mutateLoading"
                :disabled="!isValid"
                @click="save"
              >
                {{ $t('general.save') }}
              </m-btn>
            </div>
          </div>
        </m-content>
      </template>
      <template v-else>
        <m-card-item
          icon="save"
          :loading="mutateLoading"
          :padding-x="8"
          :disabled="!isValid"
          @click="save"
        >
          {{ $t('general.save') }}
        </m-card-item>
        <m-card-item
          icon="sync"
          :loading="syncLoading"
          :padding-x="8"
          :disabled="!isValid || hasError"
          @click="syncNow"
        >
          {{ $t('dataSource.syncNow') }}
        </m-card-item>
        <m-card-item
          icon="delete"
          :padding-x="8"
          :loading="deleteLoading"
          @click="deleteMsPlannerQuery"
        >
          {{ $t('general.delete') }}
        </m-card-item>
      </template>
    </template>
  </div>
</template>

<script setup>
import AlwaysSyncCheckbox from '@/components/datasource/AlwaysSyncCheckbox.vue';
import DataSourceQueryError from '@/components/datasource/DataSourceQueryError.vue';
import useConfirmDialog from '@/composables/confirm-dialog';
import useMSPlanner from '@/composables/integrations/ms-planner/ms-planner';
import useSnackBar from '@/composables/snackbar';
import { camelCase } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { customDatasourceProperty, dataSourceQueryGenericError, msPlannerAggregationMode, msPlannerAggregationResource, msPlannerFilterMode } from 'shared/constants.json';
import { isEmpty, isNullOrUndefined } from 'shared/lib/object/object';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

const snackBar = useSnackBar();
const { t } = useI18n();

const props = defineProps({
  dataSource: {
    type: Object,
    required: true,
  },
  query: {
    type: Object,
    default: () => null,
  },
  goal: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['close', 'deleted']);

const {
  createQuery,
  createQueryLoading,
  updateQuery,
  updateQueryLoading,
  deleteQuery,
  deleteQueryLoading: deleteLoading,
  getPlanDetails,
  getPlanDetailsLoading,
} = useMSPlanner();
const mutateLoading = computed(() => createQueryLoading.value || updateQueryLoading.value);

const syncLoading = ref(false);

const aggregationModes = [
  { aggregationMode: msPlannerAggregationMode.percentage, aggregationResource: msPlannerAggregationResource.task },
  { aggregationMode: msPlannerAggregationMode.percentage, aggregationResource: msPlannerAggregationResource.checklist },
  { aggregationMode: msPlannerAggregationMode.absolute, aggregationResource: msPlannerAggregationResource.task },
  { aggregationMode: msPlannerAggregationMode.absolute, aggregationResource: msPlannerAggregationResource.checklist },
].map((e) => ({
  value: e,
  text: t(`msPlanner.aggregationMode.${camelCase(e.aggregationMode)}.${camelCase(e.aggregationResource)}.label`),
  tooltip: t(`msPlanner.aggregationMode.${camelCase(e.aggregationMode)}.${camelCase(e.aggregationResource)}.description`),
}));
const localAggregation = ref(aggregationModes[0]);

const defaultMSPlannerQuery = () => ({
  aggregationMode: msPlannerAggregationMode.percentage,
  aggregationResource: msPlannerAggregationResource.task,
  filterMode: msPlannerFilterMode.filter,
  planId: null,
  planName: null,
  bucketIDs: [],
  bucketNames: [],
  labelIDs: [],
  labelNames: [],
  taskIDs: [],
  taskNames: [],
});
const localQuery = ref(defaultMSPlannerQuery());
if (props.query !== null) {
  localQuery.value = copy(props.query);
  localAggregation.value = aggregationModes.find(({ value }) => value.aggregationMode === props.query.aggregationMode && value.aggregationResource === props.query.aggregationResource);
}

const isDirty = ref(false);

const hasConnectionError = computed(() => [dataSourceQueryGenericError.incompleteSetup, dataSourceQueryGenericError.unauthenticated].includes(props.dataSource[customDatasourceProperty.dataSourceError]));
const hasDataSourceError = computed(() => !isNullOrUndefined(props.dataSource[customDatasourceProperty.dataSourceError]));
const hasQueryError = computed(() => !isDirty.value && !isNullOrUndefined(props.query?.error));
const hasError = computed(() => hasDataSourceError.value || hasQueryError.value);
const error = computed(() => {
  if (hasDataSourceError.value) {
    return props.dataSource[customDatasourceProperty.dataSourceError];
  }
  if (hasQueryError.value) {
    return props.query?.error;
  }
  return null;
});

const changeAggregation = () => {
  isDirty.value = true;
};

const selectedPlan = computed(() => {
  if (localQuery.value.planId === null) {
    return null;
  }

  const plan = props.dataSource[customDatasourceProperty.msPlannerPlans].find((r) => r.id === localQuery.value.planId);
  if (plan === undefined) {
    return null;
  }

  return plan;
});

const changePlan = () => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultMSPlannerQuery(),
    aggregationMode: localQuery.value.aggregationMode,
    aggregationResource: localQuery.value.aggregationResource,
    planId: localQuery.value.planId,
  };
  isDirty.value = true;
};

watch(selectedPlan, (newValue, oldValue) => {
  if (isNullOrUndefined(newValue)) {
    return;
  }
  if (!isNullOrUndefined(oldValue) && newValue.id === oldValue.id) {
    return;
  }
  getPlanDetails(props.dataSource, newValue).catch(() => {
    snackBar.error();
  });
}, { immediate: true });

const filterModes = msPlannerFilterMode.all.map((mode) => ({
  value: mode,
  label: t(`msPlanner.filterMode.${camelCase(mode)}.label`),
  tooltip: t(`msPlanner.filterMode.${camelCase(mode)}.description`),
}));

const changeFilterMode = (value) => {
  localQuery.value.filterMode = value;
  isDirty.value = true;
};

const buckets = computed(() => {
  if (selectedPlan.value === null) {
    return [];
  }
  if (isNullOrUndefined(selectedPlan.value.buckets)) {
    return [];
  }
  return selectedPlan.value.buckets;
});
const labels = computed(() => {
  if (selectedPlan.value === null) {
    return [];
  }
  if (isNullOrUndefined(selectedPlan.value.labels)) {
    return [];
  }
  return selectedPlan.value.labels.map((label) => {
    if (isEmpty(label.name)) {
      return { ...label, name: t(`msPlannerForm.labels.${label.id}`) };
    }
    return label;
  });
});
const tasks = computed(() => {
  if (selectedPlan.value === null) {
    return [];
  }
  if (isNullOrUndefined(selectedPlan.value.tasks)) {
    return [];
  }
  return selectedPlan.value.tasks;
});
const selectedBuckets = computed(() => {
  if (localQuery.value.bucketIDs.length === 0) {
    return [];
  }
  return buckets.value.filter((e) => localQuery.value.bucketIDs.includes(e.id));
});
const selectedLabels = computed(() => {
  if (localQuery.value.labelIDs.length === 0) {
    return [];
  }
  return labels.value.filter((e) => localQuery.value.labelIDs.includes(e.id));
});
const selectedTasks = computed(() => {
  if (localQuery.value.taskIDs.length === 0) {
    return [];
  }
  return tasks.value.filter((e) => localQuery.value.taskIDs.includes(e.id));
});
const changeBuckets = () => {
  isDirty.value = true;
};
const changeLabels = () => {
  isDirty.value = true;
};
const changeTasks = () => {
  isDirty.value = true;
};

const isValid = computed(() => selectedPlan.value !== null);

const save = () => {
  if (localQuery.value.uid === undefined) {
    createMsPlannerQuery(localQuery.value);
    return;
  }

  updateMsPlannerQuery(localQuery.value);
};
const buildFilter = () => {
  const filter = {
    bucketIDs: [],
    bucketNames: [],
    labelIDs: [],
    labelNames: [],
    taskIDs: [],
    taskNames: [],
  };
  if (localQuery.value.filterMode === msPlannerFilterMode.tasks) {
    filter.taskIDs = localQuery.value.taskIDs;
    filter.taskNames = selectedTasks.value.map(({ name }) => name);
    return filter;
  }
  filter.bucketIDs = localQuery.value.bucketIDs;
  filter.bucketNames = selectedBuckets.value.map(({ name }) => name);
  filter.labelIDs = localQuery.value.labelIDs;
  filter.labelNames = selectedLabels.value.map(({ name }) => name);
  return filter;
};
const createMsPlannerQuery = (query) => {
  createQuery({
    ...query,
    ...localAggregation.value.value,
    planName: selectedPlan.value.name,
    ...buildFilter(),
    dataSource: { uid: props.dataSource.uid },
    goal: { uid: props.goal.uid },
  }).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
  }).catch(logCatch(() => {
    snackBar.error();
  }));
};
const updateMsPlannerQuery = (query) => {
  updateQuery({
    ...query,
    ...localAggregation.value.value,
    planName: selectedPlan.value.name,
    ...buildFilter(),
    dataSource: { uid: props.dataSource.uid },
  }).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
  }).catch(logCatch(() => {
    snackBar.error();
  }));
};
const syncNow = () => {
  syncLoading.value = true;
  updateQuery(props.goal.msPlannerQuery).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
    snackBar.success(t('dataSource.successfullySynced'));
  }).catch(logCatch(() => {
    snackBar.error();
  })).finally(() => {
    syncLoading.value = false;
  });
};

const confirmDialog = useConfirmDialog();
const deleteMsPlannerQuery = () => {
  const deleteMethod = () => deleteQuery(props.goal.msPlannerQuery.uid).then(() => {
    emit('deleted');
    emit('close');
  }).catch(() => {
    snackBar.error();
  });

  confirmDialog.confirm({
    title: t('dataSource.deleteConnectionPrompt'),
    onOk() {
      deleteMethod();
    },
  });
};

</script>

<style scoped lang="scss" type="text/scss">
.ms-planner-form {
  ._item {
    margin-bottom: 1.4rem;

    ._label {
      margin-bottom: .4rem;
      color: $font-color-secondary;
    }
  }

  ._actions {
    display: flex;

    ._btns {
      display: flex;
      margin-left: auto;

      ._btn {
        margin-left: .4rem;
      }
    }
  }
}
</style>
